import { Link } from "@tanstack/react-router";
import { Alert, Badge, Card, PageHeader } from "@empite/ui-components";
import { RequireOperator } from "./guards";

/**
 * The console's front page.
 *
 * It shows the operator their own permissions before anything else, because
 * every screen behind this one renders its controls from that list and an
 * operator who does not know what they hold reads a missing button as a
 * broken page.
 */
export function Home() {
  return (
    <RequireOperator>
      {(session) => (
        <>
          <PageHeader
            title="Back office"
            description="Applications and their OAuth clients, and the people, devices and sessions on the suite."
          />
          <Card title={`Signed in as ${session.operator.display_name || session.operator.email}`}>
            <dl className="esm-detail-list">
              <dt>Email</dt>
              <dd>{session.operator.email}</dd>
              <dt>Roles</dt>
              <dd>
                {session.operator.roles.map((role) => (
                  <Badge key={role}>{role}</Badge>
                ))}
              </dd>
              <dt>Permissions</dt>
              <dd>
                <ul className="esm-scope-list">
                  {session.operator.permissions.map((permission) => (
                    <li key={permission}>
                      <code>{permission}</code>
                    </li>
                  ))}
                </ul>
              </dd>
              <dt>Session ends</dt>
              <dd>{new Date(session.expires_at).toLocaleString()}</dd>
            </dl>
          </Card>
          <Card title="Where to go">
            <ul className="esm-scope-list">
              <li>
                <Link to="/applications">Applications</Link> — status, OAuth clients and
                entitlements.
              </li>
              <li>
                <Link to="/users">Users</Link> — search an account, then its devices, bindings and
                sessions.
              </li>
              <li>
                <Link to="/routing-profiles">Routing</Link> — which provider serves a model, and in
                what order.
              </li>
              <li>
                <Link
                  to="/analytics"
                  search={{ since: undefined, until: undefined, days: undefined }}
                >
                  Analytics
                </Link>{" "}
                — traffic, tokens, people, devices and margin over a window you choose.
              </li>
              <li>
                <Link to="/guide">Integration guide</Link> — what a desktop client calls, and the
                credentials that make each call legal.
              </li>
            </ul>
          </Card>
          <Card title="Building a desktop client">
            <p>
              Sign-in, tokens, device proofs, the model list and the streaming message loop — every
              endpoint with a real request and a real response beside it. Rendered from the
              repository&rsquo;s own <code>docs/desktop-client-integration.md</code>, so it says
              what the code says.
            </p>
            <div className="esm-actions">
              {/* An anchor rather than a Button: this navigates, and a control
                  that navigates has to survive a middle-click, a new tab and a
                  screen reader announcing it as a link. The button styling is
                  borrowed; the semantics are not. */}
              <Link to="/guide" className="esm-button esm-button--primary">
                Read the integration guide
              </Link>
            </div>
          </Card>
          <Alert tone="info" title="Every change here is recorded">
            Changing anything asks you why first, and the reason is stored against your operator
            account. The server refuses a change that arrives without one, so the prompt is a
            convenience rather than the rule itself.
          </Alert>
        </>
      )}
    </RequireOperator>
  );
}
